import { useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { CircleAlert, CircleMinus, CirclePlus, Pencil, RotateCcw, TriangleAlert } from 'lucide-react'
import pluralize from 'pluralize'
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'

import { OnboardingBannerSlot } from '@/components/OnboardingBannerSlot'
import { useRequoteOnSave } from '@/components/quotes/summary/requoteOnSave'
import { Badge } from '@/components/ui/badge'
import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DateInput, DOBInput } from '@/components/ui/DateInput'
import { Dialog, DialogClose, FormDialogContent } from '@/components/ui/dialog'
import { Dropzone } from '@/components/ui/dropzone'
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Item, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '@/components/ui/item'
import { Label } from '@/components/ui/label'
import { Pagination } from '@/components/ui/pagination'
import { PhoneInput } from '@/components/ui/phoneInput'
import { ResponsiveTable, type ResponsiveTableColumn } from '@/components/ui/responsive-table'
import { SearchInput } from '@/components/ui/search-input'
import { Separator } from '@/components/ui/separator'
import { StateSelect } from '@/components/ui/StateSelect'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { validatePhone } from '@/utils/phone'
import { SEARCH_MIN_ROWS, useTableSearch } from '../hooks/useTableSearch'
import { CacheKeys, invalidateApplicationSummary } from '../lib/cache'
import { PARSER_ACCEPTED_FILE_TYPES } from '../lib/fileUpload'
import { listSentence, parseDateOnly } from '../lib/formatters'
import {
  sanitizeDriverLicenseNumber,
  sanitizeEmail,
  sanitizeLicenseState,
  sanitizeName,
  storedLicenseNumber,
} from '../lib/sanitization'
import {
  addDriver as apiAddDriver,
  approveDriver as apiApproveDriver,
  deleteDriver as apiDeleteDriver,
  designateOwnerDriver as apiDesignateOwnerDriver,
  type Driver,
  DriverStatus,
  editDriver as apiEditDriver,
  fetchRemovedDrivers,
  hasUnknownExperience,
  isDriverCarrierReady,
  isValidDriver,
  missingDriverFields,
  rejectDriver as apiRejectDriver,
  restoreDriver as apiRestoreDriver,
  undesignateOwnerDriver as apiUndesignateOwnerDriver,
} from '../models/driver'
import { uploadDrivers } from '../models/insuranceApplication'
import type { DriverMvrState } from '../models/mvr'
import { useCurrentUser } from '../models/user'
import { DriverDetailsDialog } from './DriverDetailsDialog'
import { LicenseNumberWarning } from './LicenseNumberWarning'
import { MvrStatusCell } from './Mvr/MvrStatusCell'
import { PendingChangeActions, type PendingChangeKind } from './PendingChangeActions'
import { UploadIconButton } from './UploadIconButton'

interface DriversTableProps {
  drivers: Driver[]
  setDrivers: (drivers: Driver[]) => void
  handleSubmit?: (drivers: Driver[]) => void | Promise<void>
  footer?: React.ReactNode
  onDelete?: (driver: Driver) => void
  onEdit?: (driver: Driver) => void
  onAdd?: (driver: Driver) => void
  onRestore?: (driver: Driver) => void
  addCtaOnTop?: boolean
  readOnly?: boolean
  orgId: string
  hideHeading?: boolean
  /** Defaults to the conversational 'Your drivers'; pages pass their nav label. */
  heading?: string
  /** Shows a search field above the table (customer + internal customer pages). */
  searchable?: boolean
  /** Adds an MVR column, keyed by driver id. Omit it and the column is absent. */
  mvrStates?: Map<string, DriverMvrState>
  /** Onboarding confirmation step only: requires the fields carriers need. */
  enforceCarrierRequired?: boolean
  /** Owner designation is excluded from the pending clone and writes live, so review mode hides it. */
  lockBusinessOwner?: boolean
  /** Intake only: the business owner, offered as a new driver until a driver matches them. */
  ownerAsDriver?: OwnerAsDriver | null
  /** Intake only: the owner joins as a driver through ownerAsDriver, so the dialog's checkbox goes. */
  hideOwnerToggle?: boolean
  /** Intake only: one header row (the heading, search from 5 rows, Add and Import), empty or not. */
  compact?: boolean
  /** Intake only: shown beside the compact heading, e.g. how many drivers are incomplete. */
  notice?: React.ReactNode
}

export type OwnerAsDriver = Pick<Driver, 'firstName' | 'lastName' | 'email' | 'mobile' | 'dob'>

/** The same person by email, else by full name; either way they needn't be added again. */
function isOwnerDriver(driver: Driver, owner: OwnerAsDriver): boolean {
  const clean = (value: string | null | undefined) => (value ?? '').trim().toLowerCase()
  if (clean(owner.email) && clean(driver.email) === clean(owner.email)) return true
  return (
    !!clean(owner.firstName) &&
    clean(driver.firstName) === clean(owner.firstName) &&
    clean(driver.lastName) === clean(owner.lastName)
  )
}

function driverSearchValues(driver: Driver) {
  return [
    driver.firstName,
    driver.lastName,
    driver.email,
    driver.mobile,
    driver.licenseNumber,
    driver.licenseState,
  ]
}

// The confirm checks only look at active drivers.
function driverIssue(driver: Driver): string | null {
  const missing = driver.status === DriverStatus.ACTIVE ? missingDriverFields(driver) : []
  return missing.length > 0 ? `Add ${listSentence(missing)}` : null
}

/** A required field left blank — flagged rather than dashed, since it blocks submitting. */
function MissingValue() {
  return <TriangleAlert className="text-warning-d1 h-5 w-5" />
}

function BlankValue() {
  return <span className="text-muted-foreground">—</span>
}

/** Where a row's banner says what's missing a blank name is left out; elsewhere it's flagged. */
function driverName(driver: Driver, flagBlank: boolean) {
  return (
    <>
      {flagBlank ? (
        <>
          {driver.firstName || <MissingValue />}{' '}
          {driver.lastName || (driver.firstName ? <MissingValue /> : null)}
        </>
      ) : (
        [driver.firstName, driver.lastName].filter(Boolean).join(' ') || 'Driver'
      )}
      {driver.isBusinessOwner && (
        <Badge variant="secondary" className="ml-2">
          Owner
        </Badge>
      )}
    </>
  )
}

const EXPERIENCE_CONSEQUENCE = 'Without either, insurance companies assume only 1 year of driving experience.'
const MAX_NAMED_DRIVERS = 3

/** The edit form's note under a blank CDL date: a hire date on file narrows what's still missing. */
function cdlDateNote(driver: Pick<Driver, 'cdlDate' | 'dateOfHire'>): string | null {
  if (driver.cdlDate) return null
  if (hasUnknownExperience(driver)) {
    return `Add a CDL issue date or hire date to get the best price. ${EXPERIENCE_CONSEQUENCE}`
  }
  return 'Add the CDL issue date to get the best price. Without it, insurance companies count driving experience from the hire date.'
}

function experienceWarning(drivers: Driver[]): string | null {
  const unknown = drivers.filter(hasUnknownExperience)
  if (!unknown.length) return null
  const who =
    unknown.length > MAX_NAMED_DRIVERS
      ? pluralize('driver', unknown.length, true)
      : listSentence(unknown.map((driver) => `${driver.firstName} ${driver.lastName}`.trim()))
  return `Add a CDL issue date or hire date for ${who} to get the best price. ${EXPERIENCE_CONSEQUENCE}`
}

function formatDate(value?: string | null) {
  return value ? format(parseDateOnly(value)!, 'MM/dd/yyyy') : null
}

function driverColumns(flagBlankName: boolean): ResponsiveTableColumn<Driver>[] {
  return [
    {
      key: 'name',
      header: 'Name',
      headClassName: 'w-[100px]',
      cellClassName: 'font-medium',
      // Leads the card as its title instead.
      hideOnCard: true,
      cell: (driver) => driverName(driver, flagBlankName),
    },
    { key: 'mobile', header: 'Mobile', cell: (driver) => driver.mobile || <BlankValue /> },
    { key: 'email', header: 'Email', cell: (driver) => driver.email || <BlankValue /> },
    { key: 'dob', header: 'Date of birth', cell: (driver) => formatDate(driver.dob) ?? <MissingValue /> },
    {
      key: 'dateOfHire',
      header: 'Hire date',
      cell: (driver) => formatDate(driver.dateOfHire) ?? <BlankValue />,
    },
    {
      key: 'cdlDate',
      header: 'CDL original issue date',
      cell: (driver) => formatDate(driver.cdlDate) ?? <BlankValue />,
    },
  ]
}

/** Appended only when the caller passes MVR state; other Drivers tables are unchanged. */
function mvrColumn(states: Map<string, DriverMvrState>): ResponsiveTableColumn<Driver> {
  return {
    key: 'mvr',
    header: 'MVR',
    cell: (driver) => <MvrStatusCell state={states.get(driver.id)} />,
  }
}

const removedDriverColumns: ResponsiveTableColumn<Driver>[] = [
  {
    key: 'name',
    header: 'Name',
    cellClassName: 'font-medium',
    hideOnCard: true,
    cell: (driver) => `${driver.firstName} ${driver.lastName}`,
  },
  { key: 'email', header: 'Email', cell: (driver) => driver.email || <BlankValue /> },
  { key: 'dob', header: 'Date of birth', cell: (driver) => formatDate(driver.dob) ?? <BlankValue /> },
]

export function DriversTable({
  addCtaOnTop,
  handleSubmit,
  drivers,
  setDrivers,
  footer,
  onDelete,
  onEdit,
  onAdd,
  onRestore,
  readOnly,
  orgId,
  hideHeading,
  heading = 'Your drivers',
  searchable,
  mvrStates,
  enforceCarrierRequired,
  lockBusinessOwner,
  ownerAsDriver,
  hideOwnerToggle,
  compact,
  notice,
}: DriversTableProps) {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const requote = useRequoteOnSave()
  const topRef = useRef<HTMLDivElement>(null)
  const [editDriver, setEditDriver] = useState<Driver | null>(null)
  const [addDriver, setAddDriver] = useState<Driver | null>(null)
  const [detailsDriverId, setDetailsDriverId] = useState<string | null>(null)
  // An edit started from the details view goes back to it when the form closes.
  const [editFromDetails, setEditFromDetails] = useState(false)
  const [isImporting, setIsImporting] = useState(false)
  const [removedDrivers, setRemovedDrivers] = useState<Driver[]>([])
  const [showRemoved, setShowRemoved] = useState(false)
  const { user } = useCurrentUser({ enabled: true })
  const isCaiAdmin = !!user?.isCaiAdmin
  const queryClient = useQueryClient()
  const { query, onQueryChange, page, setPage, totalPages, pageItems, rangeSummary } = useTableSearch({
    items: drivers,
    getSearchValues: driverSearchValues,
  })

  useEffect(() => {
    fetchRemovedDrivers(orgId)
      .then(setRemovedDrivers)
      .catch(() => {})
  }, [orgId])

  function getPending(driver: Driver): { id: string; kind: PendingChangeKind } | null {
    if (driver.status === DriverStatus.PENDING_ADD) {
      return { id: driver.id, kind: 'add' }
    }
    if (driver.pendingChange) {
      return {
        id: driver.pendingChange.id,
        kind: driver.pendingChange.status === DriverStatus.PENDING_REMOVE ? 'remove' : 'change',
      }
    }
    return null
  }

  async function handleApprove(pendingId: string) {
    await apiApproveDriver(orgId, pendingId)
    queryClient.invalidateQueries({ queryKey: [CacheKeys.DRIVERS, orgId] })
    invalidateApplicationSummary(queryClient, orgId)
  }

  async function handleReject(pendingId: string) {
    await apiRejectDriver(orgId, pendingId)
    queryClient.invalidateQueries({ queryKey: [CacheKeys.DRIVERS, orgId] })
    invalidateApplicationSummary(queryClient, orgId)
  }

  // Read from the live list, so the view shows a save, or the pending change it created, once it lands.
  const detailsDriver = drivers.find((driver) => driver.id === detailsDriverId) ?? null

  function openEditFromDetails(driver: Driver) {
    setDetailsDriverId(null)
    setEditFromDetails(true)
    setEditDriver(driver)
  }

  function closeEdit() {
    if (editFromDetails && editDriver) setDetailsDriverId(editDriver.id)
    setEditFromDetails(false)
    setEditDriver(null)
  }

  const hasPending = drivers.some(
    (driver) => driver.status === DriverStatus.PENDING_ADD || !!driver.pendingChange
  )

  // No row banner says what's missing unless carriers' requirements are checked, so the name flags it there.
  const flagBlankName = !enforceCarrierRequired
  const columns = useMemo(() => {
    const base = driverColumns(flagBlankName)
    return mvrStates ? [...base, mvrColumn(mvrStates)] : base
  }, [mvrStates, flagBlankName])

  function renderRowActions(driver: Driver) {
    const pending = getPending(driver)
    if (pending) {
      return (
        <PendingChangeActions
          kind={pending.kind}
          isCaiAdmin={isCaiAdmin}
          onApprove={() => handleApprove(pending.id)}
          onReject={() => handleReject(pending.id)}
        />
      )
    }
    if (readOnly) return null

    return (
      <>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="iconOnly"
              className="hover:text-brand-800 text-neutral-500"
              onClick={() => setEditDriver(driver)}
              aria-label="Edit driver"
            >
              <Pencil />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Edit driver</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="iconOnly"
              className="hover:text-error text-neutral-500"
              onClick={() => handleDelete(driver)}
              aria-label="Remove driver"
            >
              <CircleMinus />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Remove driver</TooltipContent>
        </Tooltip>
      </>
    )
  }

  async function handleDelete(driver: Driver) {
    try {
      await apiDeleteDriver(orgId, driver.id)
      queryClient.invalidateQueries({ queryKey: [CacheKeys.DRIVERS, orgId] })
      invalidateApplicationSummary(queryClient, orgId)
      fetchRemovedDrivers(orgId)
        .then(setRemovedDrivers)
        .catch(() => {})
      onDelete?.(driver)
    } catch {
      toast.error('Error deleting driver')
    }
  }

  async function handleRestore(driver: Driver) {
    try {
      await apiRestoreDriver(orgId, driver.id)
      queryClient.invalidateQueries({ queryKey: [CacheKeys.DRIVERS, orgId] })
      invalidateApplicationSummary(queryClient, orgId)
      fetchRemovedDrivers(orgId)
        .then(setRemovedDrivers)
        .catch(() => {})
      onRestore?.(driver)
    } catch {
      toast.error('Error restoring driver')
    }
  }

  async function handleEditSubmit(driver: Driver) {
    const previous = drivers.find((d) => d.id === driver.id)
    await apiEditDriver(orgId, driver.id, driver)
    let resolvedDriver = driver
    // Re-sync whenever the driver ends up flagged, not just on toggle — editing
    // an already-designated driver's name/email/etc must also resync the owner.
    // The approve endpoint re-syncs the owner from the clone, so review mode leaves it alone.
    if (
      !lockBusinessOwner &&
      previous &&
      (previous.isBusinessOwner !== driver.isBusinessOwner || driver.isBusinessOwner)
    ) {
      try {
        if (driver.isBusinessOwner) {
          await apiDesignateOwnerDriver(orgId, driver.id)
        } else {
          await apiUndesignateOwnerDriver(orgId, driver.id)
        }
      } catch (err) {
        const message =
          (err as { response?: { data?: { error?: string } } }).response?.data?.error ??
          'Failed to update business owner designation'
        toast.error(message)
        // Don't apply the requested flag locally — the server rejected it.
        resolvedDriver = { ...driver, isBusinessOwner: previous.isBusinessOwner }
      }
    }
    setDrivers(drivers.map((d) => (d.id === driver.id ? resolvedDriver : d)))
    // Re-sync from the server: under an active policy the edit doesn't touch the
    // live row (a PENDING_CHANGE clone is created instead), so the row must reload
    // to show the pending badge rather than the locally-edited values.
    queryClient.invalidateQueries({ queryKey: [CacheKeys.DRIVERS, orgId] })
    invalidateApplicationSummary(queryClient, orgId)
    closeEdit()
    onEdit?.(resolvedDriver)
  }

  async function handleAddDriverSubmit(driver: Driver) {
    let savedDriver = await apiAddDriver(orgId, driver)
    if (driver.isBusinessOwner && !lockBusinessOwner) {
      try {
        await apiDesignateOwnerDriver(orgId, savedDriver.id)
        savedDriver = { ...savedDriver, isBusinessOwner: true }
      } catch {
        toast.error('Added the driver, but not as the business owner')
      }
    }
    setDrivers([...drivers, savedDriver])
    queryClient.invalidateQueries({ queryKey: [CacheKeys.DRIVERS, orgId] })
    invalidateApplicationSummary(queryClient, orgId)
    setAddDriver(null)
    onAdd?.(savedDriver)
  }

  async function handleAllDriversSubmit() {
    setIsSubmitted(true)
    if (!allDriversValid) {
      // The error banner is above a table that can run many screens long, so bring it back into view.
      topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return
    }
    setIsConfirming(true)
    await handleSubmit?.(drivers)
    await requote?.run()
    setIsConfirming(false)
  }

  async function handleFileUpload(file: File | File[] | null) {
    if (!file || Array.isArray(file)) {
      return
    }
    // Reading the file takes a while; the same toast then reports how it went.
    const toastId = toast.loading(`Importing ${file.name}…`)
    try {
      setIsImporting(true)
      const { drivers: imported, warning } = await uploadDrivers(orgId, file)
      await queryClient.invalidateQueries({ queryKey: [CacheKeys.DRIVERS, orgId] })
      invalidateApplicationSummary(queryClient, orgId)
      if (warning) {
        toast.warning(warning, { id: toastId })
      } else {
        toast.success(`${pluralize('driver', imported.length, true)} imported`, { id: toastId })
      }
    } catch {
      toast.error('Error uploading file', { id: toastId })
    }
    setIsImporting(false)
  }

  const isDriverValid = enforceCarrierRequired ? isDriverCarrierReady : isValidDriver
  const allDriversValid = drivers.every(isDriverValid) && drivers.length > 0
  const errors = drivers.filter((driver) => !isDriverValid(driver))
  const experienceWarningText = readOnly ? null : experienceWarning(drivers)
  const offeredOwner =
    !readOnly && ownerAsDriver && !drivers.some((driver) => isOwnerDriver(driver, ownerAsDriver))
      ? ownerAsDriver
      : null

  function addOwnerAsDriver(owner: OwnerAsDriver) {
    setAddDriver({ ...createEmptyDriver(), ...owner, isBusinessOwner: true })
  }

  function renderContent() {
    if (drivers.length === 0) {
      if (readOnly) {
        return (
          <div className="mt-10 mb-10 flex w-full flex-col items-center justify-center">
            <p className="text-muted-foreground text-sm">No drivers found</p>
          </div>
        )
      }

      if (compact) return null

      return (
        <div className="mt-10 mb-10 flex w-full flex-col items-center justify-center gap-10">
          <div className="flex flex-wrap items-center justify-center gap-2">
            <Button variant="default" onClick={() => setAddDriver(createEmptyDriver())}>
              <CirclePlus />
              Add driver
            </Button>
            {offeredOwner && (
              <Button variant="outline" onClick={() => addOwnerAsDriver(offeredOwner)}>
                <CirclePlus />
                Add business owner as driver
              </Button>
            )}
          </div>

          <div className="text-muted-foreground flex items-center justify-center gap-2 text-sm">
            <Separator className="w-[100px]! shrink" /> or <Separator className="w-[100px]! shrink" />
          </div>

          <Dropzone
            onChange={handleFileUpload}
            accept={PARSER_ACCEPTED_FILE_TYPES}
            maxSizeMB={10}
            isLoading={isImporting}
            key={`${drivers?.length}-${isImporting}`}
          >
            <div className="mt-4 text-left text-sm text-gray-500">
              <p className="mb-1 font-medium">The file should include the following details:</p>
              <div className="grid gap-x-8 md:grid-cols-2">
                <ul className="list-inside list-disc space-y-1">
                  <li>Driver's name</li>
                  <li>License (State + number)</li>
                  <li>Date of birth</li>
                </ul>
                <ul className="list-inside list-disc space-y-1">
                  <li>Years of experience</li>
                  <li>Number of incidents in the past 3 years</li>
                  <li>Email and mobile number (optional)</li>
                </ul>
              </div>
            </div>
          </Dropzone>
        </div>
      )
    }

    return (
      <>
        {!readOnly && !addCtaOnTop && !compact && (
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={() => setAddDriver(createEmptyDriver())}>
              <CirclePlus />
              Add driver
            </Button>
            {offeredOwner && (
              <Button variant="outline" onClick={() => addOwnerAsDriver(offeredOwner)}>
                <CirclePlus />
                Add business owner as driver
              </Button>
            )}
            <UploadIconButton onUpload={handleFileUpload} isLoading={isImporting} label="Upload documents" />
          </div>
        )}

        <ResponsiveTable
          items={pageItems}
          columns={columns}
          getRowId={(driver) => driver.id}
          cardTitle={(driver) => driverName(driver, flagBlankName)}
          // Where rows are being finished (the review, the chat, Intake) a row opens its edit form. Elsewhere,
          // and for a row that can't be edited yet, the read-only recap, which shows what the form doesn't.
          onRowClick={(driver) =>
            enforceCarrierRequired && !readOnly && !getPending(driver)
              ? setEditDriver(driver)
              : setDetailsDriverId(driver.id)
          }
          rowActions={!readOnly || hasPending ? renderRowActions : undefined}
          empty={`No drivers match “${query}”`}
          rowIssue={enforceCarrierRequired ? driverIssue : undefined}
          caption={
            !readOnly && (footer || handleSubmit) ? (
              <div className="relative flex w-full flex-wrap items-center gap-2">
                <div className="ml-auto flex items-center gap-2">
                  {footer}
                  {handleSubmit && (
                    <Button
                      variant="default"
                      onClick={handleAllDriversSubmit}
                      loading={isConfirming ? (requote?.pendingLabel ?? 'Confirming…') : undefined}
                    >
                      {requote?.label ?? 'Done adding Drivers'}
                    </Button>
                  )}
                </div>
              </div>
            ) : undefined
          }
        />

        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          summary={rangeSummary('drivers')}
        />
        {compact && !readOnly && <div className="mt-3 flex justify-end">{rosterActions}</div>}
      </>
    )
  }

  const rosterActions = (
    <div className="flex flex-wrap items-center gap-2">
      <Button variant="link" onClick={() => setAddDriver(createEmptyDriver())}>
        <CirclePlus />
        Add driver
      </Button>
      {offeredOwner && (
        <Button variant="link" onClick={() => addOwnerAsDriver(offeredOwner)}>
          <CirclePlus />
          Add business owner as driver
        </Button>
      )}
      <UploadIconButton
        onUpload={handleFileUpload}
        isLoading={isImporting}
        label="Upload"
        ariaLabel="Upload drivers"
      />
    </div>
  )

  return (
    <>
      {compact ? (
        <div ref={topRef} className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="mr-auto flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="text-ink text-md leading-none font-semibold">{heading}</span>
            {notice}
          </div>
          {/* Kept while a query filters, or a shrinking roster would hide what it's hiding. */}
          {searchable && (drivers.length >= SEARCH_MIN_ROWS || query.length > 0) && (
            <SearchInput
              value={query}
              onValueChange={onQueryChange}
              placeholder="Search drivers..."
              aria-label="Search drivers"
            />
          )}
          {/* Up top only while there's no list yet; a populated roster has them under its rows. */}
          {!readOnly && !drivers.length && rosterActions}
        </div>
      ) : (
        <div ref={topRef} className="mb-2 flex flex-wrap items-center justify-between gap-3">
          {!hideHeading && <div className="text-2xl font-semibold">{heading}</div>}
          {searchable && !!drivers.length && (
            <SearchInput
              value={query}
              onValueChange={onQueryChange}
              className="mr-auto"
              placeholder="Search drivers..."
              aria-label="Search drivers"
            />
          )}
          {!readOnly && addCtaOnTop && !!drivers.length && rosterActions}
        </div>
      )}
      {!compact && <OnboardingBannerSlot className="mb-4" />}
      {isSubmitted && errors.length > 0 && (
        <div className="mb-3 flex w-full flex-col gap-6">
          <Item variant="outline" className="bg-error text-white">
            <ItemMedia variant="icon" className="border-none bg-transparent text-white">
              <CircleAlert className="h-10! w-10! text-white" />
            </ItemMedia>
            <ItemContent>
              <ItemTitle>Drivers with errors</ItemTitle>
              <ItemDescription className="text-white">
                {errors.map((error) => `${error.firstName} ${error.lastName}`).join(', ')}
              </ItemDescription>
            </ItemContent>
          </Item>
        </div>
      )}

      {experienceWarningText && (
        <Banner variant="warning" small className="mb-3">
          {experienceWarningText}
        </Banner>
      )}

      {renderContent()}

      {!readOnly && removedDrivers.length > 0 && (
        <div className="mt-4">
          <button
            type="button"
            className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm font-medium"
            onClick={() => setShowRemoved(!showRemoved)}
          >
            <span>{showRemoved ? '▾' : '▸'}</span>
            Removed drivers ({removedDrivers.length})
          </button>

          {showRemoved && (
            <ResponsiveTable
              className="mt-2 opacity-60"
              items={removedDrivers}
              columns={removedDriverColumns}
              getRowId={(driver) => driver.id}
              cardTitle={(driver) => `${driver.firstName} ${driver.lastName}`}
              rowActions={(driver) => (
                <Button
                  variant="iconOnly"
                  className="hover:text-cai-blue text-neutral-500"
                  onClick={() => handleRestore(driver)}
                  title="Restore"
                >
                  <RotateCcw />
                </Button>
              )}
            />
          )}
        </div>
      )}

      {editDriver && (
        <EditDriverModal
          driver={editDriver}
          existingDrivers={drivers.filter((d) => d.id !== editDriver.id)}
          onClose={closeEdit}
          onSubmit={handleEditSubmit}
          enforceCarrierRequired={enforceCarrierRequired}
          lockBusinessOwner={lockBusinessOwner}
          hideOwnerToggle={hideOwnerToggle}
        />
      )}
      {addDriver && (
        <EditDriverModal
          driver={addDriver}
          existingDrivers={drivers}
          onClose={() => setAddDriver(null)}
          onSubmit={handleAddDriverSubmit}
          enforceCarrierRequired={enforceCarrierRequired}
          isNew
          hideOwnerToggle={hideOwnerToggle}
        />
      )}
      {detailsDriver && (
        <DriverDetailsDialog
          driver={detailsDriver}
          open
          onOpenChange={(o) => !o && setDetailsDriverId(null)}
          onEdit={
            !readOnly && !getPending(detailsDriver) ? () => openEditFromDetails(detailsDriver) : undefined
          }
        />
      )}
    </>
  )
}

interface EditDriverFormData {
  firstName: string
  lastName: string
  email: string
  mobile: string
  licenseState: string
  licenseNumber: string
  dob: string
  dateOfHire: string
  cdlDate: string
  violationsCount: string
  accidentsCount: string
  suspensionsCount: string
}

/** The roster's add and edit form for a driver; onSubmit rejects with the API error to show it on the fields. */
export function EditDriverModal({
  driver,
  existingDrivers,
  onClose,
  onSubmit,
  enforceCarrierRequired,
  lockBusinessOwner,
  isNew,
  hideOwnerToggle,
  saveOnClose,
  children,
}: {
  driver: Driver
  existingDrivers: Driver[]
  onClose: () => void
  onSubmit: (driver: Driver) => Promise<void>
  enforceCarrierRequired?: boolean
  lockBusinessOwner?: boolean
  /** Set for the Add dialog, which may open prefilled (the business owner as a driver). */
  isNew?: boolean
  hideOwnerToggle?: boolean
  /** Owner-operator path: Done and closing both save what's entered, complete or not. */
  saveOnClose?: boolean
  /** Fields a caller adds after the driver's own, such as what a policy change also needs. */
  children?: ReactNode
}) {
  const isNewDriver = isNew ?? (!driver.firstName && !driver.lastName)
  const [isOwner, setIsOwner] = useState(driver.isBusinessOwner)
  const [isSaving, setIsSaving] = useState(false)

  const { control, handleSubmit, setValue, getValues, setError, trigger, formState } =
    useForm<EditDriverFormData>({
      // Opened to finish an incomplete one, its errors show at once, so each field checks again as it changes.
      mode: enforceCarrierRequired && !isNewDriver ? 'onChange' : 'onSubmit',
      defaultValues: {
        firstName: driver.firstName,
        lastName: driver.lastName,
        email: driver.email ?? '',
        mobile: driver.mobile ?? '',
        licenseState: driver.licenseState,
        licenseNumber: driver.licenseNumber,
        dob: driver.dob,
        dateOfHire: driver.dateOfHire,
        cdlDate: driver.cdlDate ?? '',
        violationsCount: driver.violationsCount?.toString() || '',
        accidentsCount: driver.accidentsCount?.toString() || '',
        suspensionsCount: driver.suspensionsCount?.toString() || '',
      },
    })

  const [typedLicenseNumber, typedLicenseState] = useWatch({
    control,
    name: ['licenseNumber', 'licenseState'],
  })

  function findDuplicateLicense(value: string, state: string): Driver | undefined {
    const target = storedLicenseNumber(value, state)
    const stateUpper = sanitizeLicenseState(state)
    if (!target || !stateUpper) return undefined
    return existingDrivers.find(
      (other) =>
        storedLicenseNumber(other.licenseNumber, other.licenseState) === target &&
        sanitizeLicenseState(other.licenseState) === stateUpper
    )
  }

  function findDuplicateEmail(value: string): Driver | undefined {
    const target = sanitizeEmail(value)
    if (!target) return undefined
    return existingDrivers.find((other) => sanitizeEmail(other.email ?? '') === target)
  }

  function applyServerErrors(err: unknown) {
    const fieldErrors = (err as { response?: { data?: Record<string, unknown> } })?.response?.data
    if (!fieldErrors || typeof fieldErrors !== 'object') {
      toast.error('Failed to save. Please try again.')
      return
    }
    const fieldMap: Record<string, keyof EditDriverFormData> = {
      email: 'email',
      mobile: 'mobile',
      first_name: 'firstName',
      last_name: 'lastName',
      license_number: 'licenseNumber',
      license_state: 'licenseState',
    }
    let surfaced = false
    Object.entries(fieldErrors).forEach(([apiField, value]) => {
      const formField = fieldMap[apiField]
      if (!formField) return
      const message = Array.isArray(value) ? String(value[0]) : String(value)
      setError(formField, { message, type: 'server' })
      surfaced = true
    })
    if (!surfaced) toast.error('Failed to save. Please try again.')
  }

  const watchedDob = useWatch({ control, name: 'dob' })
  const dobDate = parseDateOnly(watchedDob)
  const watchedHireDate = useWatch({ control, name: 'dateOfHire' })
  const watchedCdlDate = useWatch({ control, name: 'cdlDate' })
  const cdlNote = cdlDateNote({ dateOfHire: watchedHireDate, cdlDate: watchedCdlDate })

  const minHireDate = dobDate
    ? new Date(dobDate.getFullYear() + 18, dobDate.getMonth(), dobDate.getDate())
    : undefined

  async function _onSubmit(data: EditDriverFormData) {
    const violationsCount = data.violationsCount !== '' ? parseInt(data.violationsCount) : null
    const accidentsCount = data.accidentsCount !== '' ? parseInt(data.accidentsCount) : null
    const suspensionsCount = data.suspensionsCount !== '' ? parseInt(data.suspensionsCount) : null

    try {
      await onSubmit({
        ...driver,
        dob: data.dob,
        email: sanitizeEmail(data.email),
        mobile: data.mobile,
        firstName: sanitizeName(data.firstName),
        lastName: sanitizeName(data.lastName),
        licenseState: sanitizeLicenseState(data.licenseState),
        licenseNumber: sanitizeDriverLicenseNumber(data.licenseNumber),
        isBusinessOwner: isOwner,
        dateOfHire: data.dateOfHire,
        cdlDate: data.cdlDate || null,
        violationsCount,
        accidentsCount,
        suspensionsCount,
      })
    } catch (err) {
      applyServerErrors(err)
    }
  }

  async function saveAsEntered() {
    if (isSaving) return
    setIsSaving(true)
    // A date field commits a cleared value on blur, which Escape skips; blur it first so the save sees it.
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
    await _onSubmit(getValues())
    setIsSaving(false)
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!saveOnClose) onClose()
        else if (!open) void saveAsEntered()
      }}
    >
      <FormDialogContent
        title={isNewDriver ? 'Add driver' : 'Edit driver'}
        form={{
          onSubmit: saveOnClose
            ? (e) => {
                e.preventDefault()
                void saveAsEntered()
              }
            : handleSubmit(_onSubmit),
        }}
        closeDisabled={formState.isSubmitting || isSaving}
        // Opened to finish an incomplete one, it shows what's missing straight away; the fields only exist once
        // the dialog's content has mounted, which is when this fires.
        onOpenAutoFocus={() => {
          if (enforceCarrierRequired && !isNewDriver) void trigger()
        }}
        footer={
          saveOnClose ? (
            <Button type="submit" loading={isSaving ? 'Saving…' : undefined}>
              Done
            </Button>
          ) : (
            <>
              <DialogClose asChild>
                <Button type="button" variant="outline" disabled={formState.isSubmitting}>
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit">{isNewDriver ? 'Add driver' : 'Save changes'}</Button>
            </>
          )
        }
      >
        <FieldGroup>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-2">
            <Field>
              <Label htmlFor="firstName">First name</Label>
              <Controller
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    id="firstName"
                    aria-invalid={fieldState.error ? true : false}
                    onBlur={(e) => {
                      field.onBlur()
                      const sanitized = sanitizeName(e.target.value)
                      if (sanitized !== e.target.value) {
                        setValue('firstName', sanitized, { shouldValidate: true })
                      }
                    }}
                  />
                )}
                name="firstName"
                control={control}
                rules={{
                  required: true,
                }}
              />
            </Field>
            <Field>
              <Label htmlFor="lastName">Last name</Label>
              <Controller
                render={({ field, fieldState }) => (
                  <Input
                    {...field}
                    id="lastName"
                    aria-invalid={fieldState.error ? true : false}
                    onBlur={(e) => {
                      field.onBlur()
                      const sanitized = sanitizeName(e.target.value)
                      if (sanitized !== e.target.value) {
                        setValue('lastName', sanitized, { shouldValidate: true })
                      }
                    }}
                  />
                )}
                name="lastName"
                control={control}
                rules={{
                  required: true,
                }}
              />
            </Field>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-2">
            <Field>
              <FieldLabel htmlFor="mobile" optional>
                Phone number
              </FieldLabel>
              <Controller
                render={({ field, fieldState }) => (
                  <>
                    <PhoneInput
                      id="mobile"
                      value={field.value}
                      onChange={(value) => field.onChange(value || '')}
                      error={fieldState.error ? true : false}
                    />
                    {fieldState.error?.message && (
                      <p className="text-error mt-1 text-xs">{fieldState.error.message}</p>
                    )}
                  </>
                )}
                name="mobile"
                control={control}
                rules={{ validate: (value) => validatePhone(value) ?? true }}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="email" optional>
                Email
              </FieldLabel>
              <Controller
                render={({ field, fieldState }) => (
                  <>
                    <Input
                      {...field}
                      id="email"
                      type="email"
                      aria-invalid={fieldState.error ? true : false}
                      onBlur={(e) => {
                        field.onBlur()
                        const sanitized = sanitizeEmail(e.target.value)
                        if (sanitized !== e.target.value) {
                          setValue('email', sanitized, { shouldValidate: true })
                        }
                      }}
                    />
                    {fieldState.error?.message && (
                      <p className="text-error mt-1 text-xs">{fieldState.error.message}</p>
                    )}
                  </>
                )}
                name="email"
                control={control}
                rules={{
                  validate: (value: string) => {
                    const dup = findDuplicateEmail(value)
                    if (!dup) return true
                    const label = `${dup.firstName} ${dup.lastName}`.trim() || dup.email || 'another driver'
                    return `This email is already on ${label}.`
                  },
                }}
              />
            </Field>
          </div>

          {!isNewDriver && !hideOwnerToggle && (
            <Field>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="isBusinessOwner"
                  checked={isOwner}
                  disabled={!driver.email || lockBusinessOwner}
                  onCheckedChange={(checked) => setIsOwner(checked === true)}
                />
                <Label htmlFor="isBusinessOwner" className="font-normal">
                  This driver is the business owner
                </Label>
              </div>
              <p className="text-muted-foreground mt-1 text-xs">
                {lockBusinessOwner
                  ? 'Ask Cai to change who the business owner is while you are on a policy.'
                  : driver.email
                    ? 'Owner info (name, email, phone, date of birth) will be filled in from this driver instead of entered separately.'
                    : "This driver needs an email on file to be designated as owner — email can't be added here after a driver is created."}
              </p>
            </Field>
          )}

          <Field>
            <FieldLabel htmlFor="licenseState" optional={!enforceCarrierRequired}>
              Commercial driver's license
            </FieldLabel>
            <div className="grid grid-cols-[100px_1fr] gap-2">
              <Controller
                render={({ field, fieldState }) => (
                  <StateSelect
                    error={fieldState.error ? true : false}
                    value={field.value}
                    onChange={(value: string) => {
                      field.onChange(sanitizeLicenseState(value))
                      trigger('licenseNumber')
                    }}
                    clearable
                  />
                )}
                name="licenseState"
                control={control}
                rules={{
                  required: enforceCarrierRequired,
                  pattern: /^[A-Z]{2}$/i,
                }}
              />
              <Controller
                render={({ field, fieldState }) => (
                  <>
                    <Input
                      {...field}
                      placeholder="License number"
                      aria-invalid={fieldState.error ? true : false}
                      onBlur={(e) => {
                        field.onBlur()
                        const sanitized = sanitizeDriverLicenseNumber(e.target.value)
                        if (sanitized !== e.target.value) {
                          setValue('licenseNumber', sanitized, { shouldValidate: true })
                        }
                      }}
                    />
                    {fieldState.error?.message && (
                      <p className="text-error mt-1 text-xs">{fieldState.error.message}</p>
                    )}
                  </>
                )}
                name="licenseNumber"
                control={control}
                rules={{
                  required: enforceCarrierRequired,
                  validate: {
                    // Half a license is worse than none: carriers substitute their own state for a
                    // blank one, so a number without a state is submitted against the wrong state.
                    statePairing: (value: string) => {
                      const state = getValues('licenseState')
                      if (value && !state) return 'Select the state that issued this license.'
                      if (state && !value) return 'Enter the license number, or clear the state.'
                      return true
                    },
                    validLicense: (value: string) =>
                      !value || !!sanitizeDriverLicenseNumber(value) || 'Enter a valid license number.',
                    noDuplicate: (value: string) => {
                      const state = getValues('licenseState')
                      const dup = findDuplicateLicense(value, state)
                      if (!dup) return true
                      const label = `${dup.firstName} ${dup.lastName}`.trim() || dup.licenseNumber
                      return `This license is already on driver ${label}.`
                    },
                  },
                }}
              />
            </div>
            {!formState.errors.licenseNumber && (
              <LicenseNumberWarning
                licenseNumber={typedLicenseNumber ?? ''}
                licenseState={typedLicenseState ?? ''}
              />
            )}
          </Field>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-2">
            <Field>
              <Label htmlFor="dob">Date of birth</Label>
              <Controller
                render={({ field, fieldState }) => (
                  <DOBInput
                    {...field}
                    value={parseDateOnly(field.value)}
                    onChange={(date) => field.onChange(date ?? '')}
                    inputProps={{ id: 'dob', 'aria-invalid': fieldState.error ? true : false }}
                    minAge={18}
                  />
                )}
                name="dob"
                control={control}
                rules={{
                  required: true,
                }}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="dateOfHire" optional>
                Date of hire
              </FieldLabel>
              <Controller
                render={({ field, fieldState }) => (
                  <DateInput
                    {...field}
                    value={parseDateOnly(field.value)}
                    onChange={(date) => field.onChange(date ?? '')}
                    inputProps={{ id: 'dateOfHire', 'aria-invalid': fieldState.error ? true : false }}
                    hidden={[{ after: new Date() }, ...(minHireDate ? [{ before: minHireDate }] : [])]}
                    startMonth={minHireDate}
                    errorMessage="Hire date must be after driver turned 18"
                  />
                )}
                name="dateOfHire"
                control={control}
              />
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="cdlDate" optional>
              CDL original issue date
            </FieldLabel>
            <Controller
              render={({ field, fieldState }) => (
                <DateInput
                  {...field}
                  value={parseDateOnly(field.value)}
                  // '' not undefined: the Controller reads an undefined value as its default, the old date.
                  onChange={(date) => field.onChange(date ?? '')}
                  inputProps={{
                    id: 'cdlDate',
                    'aria-invalid': fieldState.error ? true : false,
                  }}
                  hidden={[{ after: new Date() }]}
                />
              )}
              name="cdlDate"
              control={control}
            />
            {cdlNote && <p className="text-warning-d1 mt-1 text-xs">{cdlNote}</p>}
            <p className="text-muted-foreground mt-1 text-xs">
              Best estimation is okay. Use last day of the month if you cannot remember exact date.
            </p>
          </Field>

          <div className="mt-4">
            {/* A heading over three fields, not one field's label, styled to read as FieldLabel's optional. */}
            <p className="mb-3 flex gap-2 text-sm font-medium">
              Number of incidents in the past 3 years
              <span className="text-ink-400 dark:text-muted-foreground">(optional)</span>
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-1">
              <Field>
                <Label htmlFor="violationsCount">Violations</Label>
                <Controller
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      id="violationsCount"
                      type="number"
                      min={0}
                      placeholder="0"
                      aria-invalid={fieldState.error ? true : false}
                    />
                  )}
                  name="violationsCount"
                  control={control}
                />
              </Field>
              <Field>
                <Label htmlFor="accidentsCount">At-fault accidents</Label>
                <Controller
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      id="accidentsCount"
                      type="number"
                      min={0}
                      placeholder="0"
                      aria-invalid={fieldState.error ? true : false}
                    />
                  )}
                  name="accidentsCount"
                  control={control}
                />
              </Field>
              <Field>
                <Label htmlFor="suspensionsCount">Suspensions</Label>
                <Controller
                  render={({ field, fieldState }) => (
                    <Input
                      {...field}
                      id="suspensionsCount"
                      type="number"
                      min={0}
                      placeholder="0"
                      aria-invalid={fieldState.error ? true : false}
                    />
                  )}
                  name="suspensionsCount"
                  control={control}
                />
              </Field>
            </div>
          </div>
          {children}
        </FieldGroup>
        {!enforceCarrierRequired && (
          <p className="text-muted-foreground text-sm">Missing something? You can add it later</p>
        )}
      </FormDialogContent>
    </Dialog>
  )
}

export function createEmptyDriver(): Driver {
  return {
    id: uuidv4(),
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    licenseNumber: '',
    licenseState: '',
    dob: '',
    status: DriverStatus.ACTIVE,
    dateOfHire: '',
    dateOnPolicy: '',
    dateCreated: '',
    dateUpdated: '',
    telematicsId: '',
    cdl: '',
    cdlDate: null,
    violationsCount: null,
    accidentsCount: null,
    suspensionsCount: null,
    isBusinessOwner: false,
  }
}
